import { handleRequest } from "../../axios";
import { axiosInstance } from "./JsdhNotifikaceService";
import {
    ADMIN_SIA_ACCOUNTS_URL,
    ADMIN_SIA_ACCOUNT_PREVIEW_ID_URL,
    ADMIN_SIA_ACCOUNT_BY_ID_URL,
    ADMIN_SIA_ACCOUNT_PASSWORD_URL,
    ADMIN_SIA_ACCOUNT_REGENERATE_PASSWORD_URL,
    ADMIN_SIA_ACCOUNTS_BY_OBJEKT_URL,
    ADMIN_SIA_ACCOUNT_MUTE_URL,
    ADMIN_SIA_ZONES_URL,
    ADMIN_SIA_ZONE_BY_ID_URL,
    ADMIN_SIA_ZONES_BY_ACCOUNT_URL,
    ADMIN_SIA_ZONE_CREATE_URL,
    ADMIN_SIA_ZONE_MUTE_URL,
    ADMIN_SIA_EVENTS_BY_ACCOUNT_URL,
    ADMIN_SIA_EVENTS_BY_ZONE_URL,
    ADMIN_SIA_CODES_URL,
    ADMIN_SIA_CODE_BY_ID_URL,
    ADMIN_RELAY_CONFIG_URL,
    ADMIN_RELAY_CONFIG_BY_ID_URL,
    ADMIN_RELAY_CONFIG_BY_OBJEKT_URL,
    ADMIN_RELAY_ON_URL,
    ADMIN_RELAY_OFF_URL,
    ADMIN_RELAY_PULSE_URL,
} from "../url";
import type {
    SiaAccount,
    SiaAccountDetail,
    CreateSiaAccountRequest,
    UpdateSiaAccountRequest,
    SiaAccountPasswordResponse,
    SiaZone,
    CreateSiaZoneRequest,
    UpdateSiaZoneRequest,
    SiaMuteRequest,
    SiaMuteResponse,
    SiaEvent,
    SiaCodeKonfig,
    RelayKonfig,
    CreateRelayKonfigRequest,
    UpdateRelayKonfigRequest,
    RelayCommandRequest,
    RelayCommandResponse,
} from "../generated";

// =====================
// SIA ACCOUNTS
// =====================

const getAllSiaAccounts = () => {
    return handleRequest<SiaAccount[]>(() =>
        axiosInstance.get(ADMIN_SIA_ACCOUNTS_URL)
    );
};

export interface PreviewAccountIdResponse {
    accountId: string;
    format: string;
    info: string;
    error?: string;
}

const previewAccountId = (objektId: number) => {
    return handleRequest<PreviewAccountIdResponse>(() =>
        axiosInstance.get(ADMIN_SIA_ACCOUNT_PREVIEW_ID_URL(objektId))
    );
};

const getSiaAccountById = (id: number) => {
    return handleRequest<SiaAccountDetail>(() =>
        axiosInstance.get(ADMIN_SIA_ACCOUNT_BY_ID_URL(id))
    );
};

const getSiaAccountsByObjekt = (objektId: number) => {
    return handleRequest<SiaAccount[]>(() =>
        axiosInstance.get(ADMIN_SIA_ACCOUNTS_BY_OBJEKT_URL(objektId))
    );
};

const createSiaAccount = (data: CreateSiaAccountRequest) => {
    return handleRequest<SiaAccount>(() =>
        axiosInstance.post(ADMIN_SIA_ACCOUNTS_URL, data)
    );
};

const updateSiaAccount = (id: number, data: UpdateSiaAccountRequest) => {
    return handleRequest<SiaAccount>(() =>
        axiosInstance.put(ADMIN_SIA_ACCOUNT_BY_ID_URL(id), data)
    );
};

const deleteSiaAccount = (id: number) => {
    return handleRequest<void>(() =>
        axiosInstance.delete(ADMIN_SIA_ACCOUNT_BY_ID_URL(id))
    );
};

const getSiaAccountPassword = (id: number) => {
    return handleRequest<SiaAccountPasswordResponse>(() =>
        axiosInstance.get(ADMIN_SIA_ACCOUNT_PASSWORD_URL(id))
    );
};

const regenerateSiaAccountPassword = (id: number) => {
    return handleRequest<SiaAccountPasswordResponse>(() =>
        axiosInstance.post(ADMIN_SIA_ACCOUNT_REGENERATE_PASSWORD_URL(id))
    );
};

const muteSiaAccount = (id: number, data: SiaMuteRequest) => {
    return handleRequest<SiaMuteResponse>(() =>
        axiosInstance.post(ADMIN_SIA_ACCOUNT_MUTE_URL(id), data)
    );
};

const unmuteSiaAccount = (id: number) => {
    return handleRequest<SiaMuteResponse>(() =>
        axiosInstance.delete(ADMIN_SIA_ACCOUNT_MUTE_URL(id))
    );
};

// =====================
// SIA ZONES
// =====================

const getAllSiaZones = () => {
    return handleRequest<SiaZone[]>(() =>
        axiosInstance.get(ADMIN_SIA_ZONES_URL)
    );
};

const getSiaZoneById = (id: number) => {
    return handleRequest<SiaZone>(() =>
        axiosInstance.get(ADMIN_SIA_ZONE_BY_ID_URL(id))
    );
};

const getSiaZonesByAccount = (accountId: number) => {
    return handleRequest<SiaZone[]>(() =>
        axiosInstance.get(ADMIN_SIA_ZONES_BY_ACCOUNT_URL(accountId))
    );
};

const createSiaZone = (data: CreateSiaZoneRequest) => {
    return handleRequest<SiaZone>(() =>
        axiosInstance.post(ADMIN_SIA_ZONE_CREATE_URL(data.siaAccountId), data)
    );
};

const updateSiaZone = (id: number, data: UpdateSiaZoneRequest) => {
    return handleRequest<SiaZone>(() =>
        axiosInstance.put(ADMIN_SIA_ZONE_BY_ID_URL(id), data)
    );
};

const deleteSiaZone = (id: number) => {
    return handleRequest<void>(() =>
        axiosInstance.delete(ADMIN_SIA_ZONE_BY_ID_URL(id))
    );
};

const muteSiaZone = (id: number, data: SiaMuteRequest) => {
    return handleRequest<SiaMuteResponse>(() =>
        axiosInstance.post(ADMIN_SIA_ZONE_MUTE_URL(id), data)
    );
};

const unmuteSiaZone = (id: number) => {
    return handleRequest<SiaMuteResponse>(() =>
        axiosInstance.delete(ADMIN_SIA_ZONE_MUTE_URL(id))
    );
};

// =====================
// SIA EVENTS
// =====================

const getSiaEventsByAccount = (accountId: number, limit?: number) => {
    return handleRequest<SiaEvent[]>(() =>
        axiosInstance.get(ADMIN_SIA_EVENTS_BY_ACCOUNT_URL(accountId, limit))
    );
};

const getSiaEventsByZone = (zoneId: number, limit?: number) => {
    return handleRequest<SiaEvent[]>(() =>
        axiosInstance.get(ADMIN_SIA_EVENTS_BY_ZONE_URL(zoneId, limit))
    );
};

// =====================
// SIA CODE KONFIGURACE
// =====================

const getAllSiaCodes = () => {
    return handleRequest<SiaCodeKonfig[]>(() =>
        axiosInstance.get(ADMIN_SIA_CODES_URL)
    );
};

const getSiaCodeById = (id: number) => {
    return handleRequest<SiaCodeKonfig>(() =>
        axiosInstance.get(ADMIN_SIA_CODE_BY_ID_URL(id))
    );
};

const updateSiaCode = (id: number, data: Partial<SiaCodeKonfig>) => {
    return handleRequest<SiaCodeKonfig>(() =>
        axiosInstance.put(ADMIN_SIA_CODE_BY_ID_URL(id), data)
    );
};

// =====================
// RELAY KONFIGURACE
// =====================

const getAllRelayKonfig = () => {
    return handleRequest<RelayKonfig[]>(() =>
        axiosInstance.get(ADMIN_RELAY_CONFIG_URL)
    );
};

const getRelayKonfigById = (id: number) => {
    return handleRequest<RelayKonfig>(() =>
        axiosInstance.get(ADMIN_RELAY_CONFIG_BY_ID_URL(id))
    );
};

const getRelayKonfigByObjekt = (objektId: number) => {
    return handleRequest<RelayKonfig[]>(() =>
        axiosInstance.get(ADMIN_RELAY_CONFIG_BY_OBJEKT_URL(objektId))
    );
};

const createRelayKonfig = (data: CreateRelayKonfigRequest) => {
    return handleRequest<RelayKonfig>(() =>
        axiosInstance.post(ADMIN_RELAY_CONFIG_URL, data)
    );
};

const updateRelayKonfig = (id: number, data: UpdateRelayKonfigRequest) => {
    return handleRequest<RelayKonfig>(() =>
        axiosInstance.put(ADMIN_RELAY_CONFIG_BY_ID_URL(id), data)
    );
};

const deleteRelayKonfig = (id: number) => {
    return handleRequest<void>(() =>
        axiosInstance.delete(ADMIN_RELAY_CONFIG_BY_ID_URL(id))
    );
};

const sendRelayCommand = (objektId: number, relayZoneId: number, data: RelayCommandRequest) => {
    // Vybrat spravny endpoint podle typu prikazu
    let url: string;
    const params: Record<string, string> = {};

    switch (data.command) {
        case 'ON':
            url = ADMIN_RELAY_ON_URL(objektId, relayZoneId);
            break;
        case 'OFF':
            url = ADMIN_RELAY_OFF_URL(objektId, relayZoneId);
            break;
        case 'PULSE':
            url = ADMIN_RELAY_PULSE_URL(objektId, relayZoneId);
            if (data.durationMs) {
                params.durationMs = data.durationMs.toString();
            }
            break;
        default:
            throw new Error(`Unknown relay command: ${data.command}`);
    }

    return handleRequest<RelayCommandResponse>(() =>
        axiosInstance.post(url, null, { params })
    );
};

// =====================
// EXPORT
// =====================

const SiaService = {
    // SIA Accounts
    getAllSiaAccounts,
    previewAccountId,
    getSiaAccountById,
    getSiaAccountsByObjekt,
    createSiaAccount,
    updateSiaAccount,
    deleteSiaAccount,
    getSiaAccountPassword,
    regenerateSiaAccountPassword,
    muteSiaAccount,
    unmuteSiaAccount,
    // SIA Zones
    getAllSiaZones,
    getSiaZoneById,
    getSiaZonesByAccount,
    createSiaZone,
    updateSiaZone,
    deleteSiaZone,
    muteSiaZone,
    unmuteSiaZone,
    // SIA Events
    getSiaEventsByAccount,
    getSiaEventsByZone,
    // SIA Codes
    getAllSiaCodes,
    getSiaCodeById,
    updateSiaCode,
    // Relay
    getAllRelayKonfig,
    getRelayKonfigById,
    getRelayKonfigByObjekt,
    createRelayKonfig,
    updateRelayKonfig,
    deleteRelayKonfig,
    sendRelayCommand,
};

export default SiaService;
